import { zodResolver } from "@hookform/resolvers/zod";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import { useId, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import useSWRMutation from "swr/mutation";

import type {
	BillingBulkUpdateContext,
	BillingType,
	DealBillingContext
} from "~/models/DealBilling";
import { billingBulkUpdateSchema } from "~/models/DealBilling";
import { isSuccess } from "~/utils/api/api_handler";
import { postUpdate } from "~/utils/api/deals/DealBillingUpdate";
import { generatePath } from "~/utils/generatePath";

type Props = {
	billingContext: DealBillingContext;
	billingType: BillingType;
	onClose: () => void;
};

const TRUNCATE_SCALE_LABELS = {
	0: "1円未満を切り捨て",
	1: "10円未満を切り捨て",
	2: "100円未満を切り捨て",
	3: "1,000円未満を切り捨て"
} as const satisfies Record<number, string>;

/**
 * 単価の一括変更モーダル
 * @for client
 */
export function ChangeAmountModal({
	billingContext,
	onClose,
	billingType
}: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);
		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}
		return parsed;
	});
	const [step, setStep] = useState<"input" | "confirm">("input");

	const {
		formState: { errors },
		handleSubmit,
		register,
		control
	} = useForm<BillingBulkUpdateContext>({
		defaultValues: { scale: 1.2, truncateScale: 2 },
		mode: "onChange",
		resolver: zodResolver(billingBulkUpdateSchema)
	});
	const scale = useWatch({ control, name: "scale" });
	const truncateScale = useWatch({ control, name: "truncateScale" });

	const { trigger, isMutating } = useSWRMutation(
		{
			params: { billingType },
			url: generatePath("/deals/:deal_id/deal_billings", { deal_id: id })
		},
		async () => postUpdate({ scale, truncateScale }, id, billingType)
	);

	const onSubmit = handleSubmit(() => {
		setStep("confirm");
	});

	const onBack = (): void => {
		setStep("input");
	};

	const onAcceptUpdate = async (): Promise<void> => {
		const result = await trigger();
		if (isSuccess(result)) {
			onClose();
			notify({ content: "単価を変更しました。", variant: "success" });
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};

	return (
		<Modalv2 isOpen onClose={onClose} size="s">
			<ModalHeader title="施工単価の一括変更" />
			<ModalBody>
				{step === "input" && billingContext.dealSettlements.length > 0 && (
					<Alert mb={designTokens["spacing/s"]} variant="warning">
						精算項目には反映されません。
					</Alert>
				)}
				{step === "confirm" && (
					<>
						<Paragraph>以下の内容で施工単価を一括変更します。</Paragraph>
						<Alert my={designTokens["spacing/s"]} variant="warning">
							この変更は元に戻せません。
						</Alert>
					</>
				)}
				<form id={formId} noValidate onSubmit={onSubmit}>
					<FormGroup isConfirmation={step === "confirm"} variant="vertical">
						<FormControl
							chip="必須"
							isInvalid={step === "input" && !!errors.scale}
							label="倍率"
						>
							<Flex alignItems="center" gap={designTokens["spacing/xxs"]}>
								<Input
									type="number"
									width="4rem"
									{...register("scale", {
										valueAsNumber: true
									})}
								/>
								<span>倍</span>
							</Flex>
							<FormControlText
								error={errors.scale?.message}
								help="元の単価×指定した倍率に変更します。"
							/>
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.truncateScale}
							label="切り捨て設定"
						>
							<InputSelect
								{...register("truncateScale", {
									valueAsNumber: true
								})}
							>
								{Object.entries(TRUNCATE_SCALE_LABELS).map(([value, label]) => (
									<option key={value} value={value}>
										{label}
									</option>
								))}
							</InputSelect>
						</FormControl>
					</FormGroup>
				</form>
			</ModalBody>
			{step === "input" ? (
				<ModalFooter
					leftArea={
						<Button onClick={onClose} type="button" variant="secondary">
							キャンセル
						</Button>
					}
					rightArea={
						<Button form={formId} type="submit" variant="primary">
							確認
						</Button>
					}
				/>
			) : (
				<ModalFooter
					leftArea={
						<Button
							disabled={isMutating}
							onClick={onBack}
							type="button"
							variant="secondary"
						>
							もどる
						</Button>
					}
					rightArea={
						<Button
							loading={isMutating}
							onClick={onAcceptUpdate}
							type="button"
							variant="primary"
						>
							変更
						</Button>
					}
				/>
			)}
		</Modalv2>
	);
}
